import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import {
  Search, Globe, Link2, FileText, Send, Zap, ShieldCheck, Clock,
  ArrowLeft, Loader2, CheckCircle2, Package, Handshake, ClipboardCheck,
  Boxes, Truck, Calculator, MessageCircle, ExternalLink, X, User, LogOut,
  ShoppingCart, ChevronRight, ChevronLeft, Minus, Plus, ZoomIn, Share2, Heart, Camera, FileDown, Landmark
} from 'lucide-react';
import { downloadPdf } from '../lib/download';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CURRENCIES, currencyMeta, getStoredCurrency, formatFx } from '../lib/currency';
import FreightEstimator from '../components/FreightEstimator';
import { KeywordSearch, PhotoSearch } from '../components/ProductSearch';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUM = '233544205844';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUM}`;
const SKU_PREVIEW = 8;

const SEA_RATE_PER_CBM = 240;
const AIR_RATE_PER_KG = 8.0;
const HANDLING_FEE = 35;
const QC_PHOTO_FEE = 25;
const FACTORY_AUDIT_FEE = 180;

const STEPS = [
  { num: '01', title: 'Link / RFQ', desc: 'Paste 1688 product links or upload your specification sheet.', icon: Link2 },
  { num: '02', title: 'Negotiation', desc: 'Our native Chinese team negotiates direct wholesale pricing.', icon: Handshake },
  { num: '03', title: 'Warehouse QC', desc: 'Goods arrive at our Shanghai hub for strict inspection.', icon: ClipboardCheck },
  { num: '04', title: 'Consolidation', desc: 'We consolidate orders into single containers.', icon: Boxes },
  { num: '05', title: 'Global Transit', desc: 'Customs clearance and dispatch to your door.', icon: Truck },
];

export default function SourcingPortal() {
  const { client, logout } = useClientAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('link');
  const [url, setUrl] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedProduct, setParsedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(7.24);
  const [fx, setFx] = useState(null);
  const [currency, setCurrency] = useState(getStoredCurrency());

  useEffect(() => {
    if (client?.currency && !localStorage.getItem('rtr_currency')) { setCurrency(client.currency); }
  }, [client]);
  const changeCurrency = (code) => { setCurrency(code); localStorage.setItem('rtr_currency', code); };

  useEffect(() => {
    const load = () => fetch(`${API}/api/sourcing/exchange-rate`).then((r) => r.json())
      .then((d) => { if (d.fx) { setFx(d.fx); if (d.fx.usd_rmb) setExchangeRate(d.fx.usd_rmb); } }).catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const fmtGhs = (rmb) => formatFx(fx, rmb, currency);
  const curRate = fx?.currencies?.[currency];

  /* Calculator */
  const [calcWeight, setCalcWeight] = useState(150);
  const [calcCbm, setCalcCbm] = useState(0.8);
  const [calcMethod, setCalcMethod] = useState('sea');
  const [calcQc, setCalcQc] = useState(true);
  const [calcAudit, setCalcAudit] = useState(false);

  /* RFQ */
  const [rfqName, setRfqName] = useState('');
  const [rfqQty, setRfqQty] = useState('');
  const [rfqService, setRfqService] = useState('Full Sourcing & Shipping');
  const [rfqSubmitting, setRfqSubmitting] = useState(false);

  /* Order modal state */
  const [orderQty, setOrderQty] = useState(1);
  const [selectedSkus, setSelectedSkus] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [activeGalleryIdx, setActiveGalleryIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  /* Link detected but live data unavailable → manual entry */
  const [linkInfo, setLinkInfo] = useState(null);
  const [manualTitle, setManualTitle] = useState('');
  const [manualPrice, setManualPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [proformaBusy, setProformaBusy] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [pendingQc, setPendingQc] = useState(0);
  const [autoPlace, setAutoPlace] = useState(false);
  const [resuming, setResuming] = useState(false);
  const orderSectionRef = useRef(null);
  const [orderSectionVisible, setOrderSectionVisible] = useState(false);

  useEffect(() => {
    if (!showModal) { setOrderSectionVisible(false); return; }
    const el = orderSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(([entry]) => setOrderSectionVisible(entry.isIntersecting), { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [showModal, parsedProduct, placedOrder]);

  const savePendingOrder = () => {
    const p = parsedProduct;
    sessionStorage.setItem('rtr_pending_order', JSON.stringify({
      url: p.source_url || url, selectedSkus, orderQty, specialInstructions,
      manual: p.manual_entry ? { title: p.title, price_rmb: p.price_rmb, platform: p.platform, item_id: p.item_id, exchange_rate: p.exchange_rate } : null,
    }));
  };

  useEffect(() => {
    if (!client) return;
    const raw = sessionStorage.getItem('rtr_pending_order');
    if (!raw) return;
    sessionStorage.removeItem('rtr_pending_order');
    let pending; try { pending = JSON.parse(raw); } catch { return; }
    setResuming(true);
    (async () => {
      try {
        const res = await fetch(`${API}/api/sourcing/parse-url`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: pending.url }) });
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || 'Could not reload your product');
        if (json.success) openProduct(json.data);
        else if (pending.manual) openProduct({ ...json.data, ...pending.manual, price_usd: pending.manual.price_rmb ? Number((pending.manual.price_rmb / (json.data.exchange_rate || 7.24)).toFixed(2)) : 0, gallery_images: [], detail_images: [], sku_options: [], sku_list: [], specs: [], domestic_shipping_rmb: 0, manual_entry: true });
        else throw new Error('Could not reload your product — please paste the link again');
        setSelectedSkus(pending.selectedSkus || {});
        setOrderQty(pending.orderQty || 1);
        setSpecialInstructions(pending.specialInstructions || '');
        setAutoPlace(true);
      } catch (err) { toast.error(err.message); }
      finally { setResuming(false); }
    })();
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoPlace && parsedProduct && showModal && !orderSubmitting) { setAutoPlace(false); placeOrder(true); }
  }, [autoPlace, parsedProduct, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!client) { setPendingQc(0); return; }
    fetch(`${API}/api/orders/my`, { headers: { Authorization: `Bearer ${localStorage.getItem('client_token')}` } })
      .then((r) => r.json())
      .then((d) => setPendingQc((d.data || []).filter((o) => o.qc_review?.status === 'pending_approval').length))
      .catch(() => {});
  }, [client]);

  const imgSrc = (u) => (u ? `${API}/api/sourcing/img?u=${encodeURIComponent(u)}` : '');
  const allImages = parsedProduct ? [...(parsedProduct.gallery_images || []), ...(parsedProduct.detail_images || [])] : [];
  const skuLabel = (v) => (typeof v === 'string' ? v : v.en);
  const skuValueForOrder = (v) => (typeof v === 'string' ? v : (v.en !== v.zh ? `${v.en} (${v.zh})` : v.zh));
  const matchedSku = parsedProduct?.sku_list?.find((s) => {
    const keys = Object.keys(s.props || {});
    return keys.length > 0 && keys.every((k) => selectedSkus[k] === s.props[k]);
  });
  const tierPrice = (parsedProduct?.price_tiers || []).filter((t) => orderQty >= t.min_qty).sort((a, b) => b.min_qty - a.min_qty)[0]?.price_rmb;
  const unitPrice = matchedSku?.price_rmb || tierPrice || parsedProduct?.price_rmb || 0;

  const selectSku = (optName, label, image) => {
    setSelectedSkus((prev) => ({ ...prev, [optName]: label }));
    const idx = image ? allImages.indexOf(image) : -1;
    if (idx >= 0) setActiveGalleryIdx(idx);
  };

  const shareToWhatsApp = async () => {
    if (client) { placeOrder(); return; }
    const p = parsedProduct;
    const skuText = Object.entries(selectedSkus).map(([k, v]) => `  - ${k}: ${v}`).join('\n');
    const lines = [
      '*Product Inquiry - RunTheRays Sourcing*',
      '',
      `*${p.title}*`,
      p.title_zh && p.title_zh !== p.title ? p.title_zh : null,
      '',
      `Platform: ${p.platform}${p.item_id ? `  |  Item ID: ${p.item_id}` : ''}`,
      unitPrice > 0 ? `Unit Price: ¥${unitPrice.toFixed(2)}${fmtGhs(unitPrice) ? ` (≈ ${fmtGhs(unitPrice)})` : ''} (~$${(unitPrice / exchangeRate).toFixed(2)} USD)` : 'Unit Price: to be confirmed',
      `Quantity: ${orderQty}`,
      skuText ? `Options:\n${skuText}` : null,
      p.moq ? `MOQ: ${p.moq}` : null,
      '',
      p.source_url ? `Listing: ${p.source_url}` : null,
      allImages[activeGalleryIdx] ? `Photo: ${allImages[activeGalleryIdx]}` : null,
      '',
      'Please help me source this item.',
    ].filter((l) => l !== null);
    const waWin = window.open('', '_blank');
    let agent = WHATSAPP_NUM;
    try { const r = await fetch(`${API}/api/sourcing/next-agent`); const j = await r.json(); if (j.whatsapp) agent = j.whatsapp; } catch { /* fall back to default number */ }
    const waUrl = `https://wa.me/${agent}?text=${encodeURIComponent(lines.join('\n'))}`;
    if (waWin) waWin.location.href = waUrl; else window.location.href = waUrl;
  };

  const [resultLoadingId, setResultLoadingId] = useState(null);
  const [expandedSku, setExpandedSku] = useState({});
  const openSearchResult = async (item) => {
    setResultLoadingId(item.item_id);
    try {
      const res = await fetch(`${API}/api/sourcing/parse-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: item.source_url }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Could not load product');
      if (!json.success) {
        openProduct({ ...json.data, title: item.title, title_zh: '', price_rmb: item.price_rmb, price_usd: item.price_usd || 0,
          gallery_images: item.image ? [item.image] : [], detail_images: [], sku_options: [], sku_list: [], specs: [],
          moq: item.moq, domestic_shipping_rmb: 0, manual_entry: true });
        toast.message('Full SKU details unavailable — showing listing summary');
        return;
      }
      openProduct(json.data);
    } catch (err) { toast.error(err.message); }
    finally { setResultLoadingId(null); }
  };

  const openProduct = (data) => {
    setParsedProduct(data);
    setExchangeRate(data.exchange_rate || 7.24);
    setSelectedSkus({});
    setOrderQty(data.moq_qty || 1);
    setSpecialInstructions('');
    setActiveGalleryIdx(0);
    setSaved(false);
    setPlacedOrder(null);
    setExpandedSku({});
    setShowModal(true);
  };

  const openManualProduct = (e) => {
    e.preventDefault();
    const price = Number(manualPrice) || 0;
    openProduct({
      ...linkInfo.data,
      title: manualTitle.trim(),
      price_rmb: price,
      price_usd: price ? Number((price / (linkInfo.data.exchange_rate || 7.24)).toFixed(2)) : 0,
      gallery_images: [], detail_images: [], sku_options: [], sku_list: [], specs: [],
      domestic_shipping_rmb: 0, manual_entry: true,
    });
  };

  const freight = calcMethod === 'sea' ? calcCbm * SEA_RATE_PER_CBM : calcWeight * AIR_RATE_PER_KG;
  const qcCost = (calcQc ? QC_PHOTO_FEE : 0) + (calcAudit ? FACTORY_AUDIT_FEE : 0);
  const total = freight + HANDLING_FEE + qcCost;

  const handlePaste = useCallback(async () => {
    try { const t = await navigator.clipboard.readText(); if (t) setUrl(t); } catch { toast.error('Please paste directly.'); }
  }, []);

  const handleParse = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setParsing(true);
    setLinkInfo(null);
    try {
      const res = await fetch(`${API}/api/sourcing/parse-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to parse URL');
      if (!json.success) {
        setLinkInfo(json);
        setManualTitle(''); setManualPrice('');
        return;
      }
      openProduct(json.data);
    } catch (err) { toast.error(err.message); }
    finally { setParsing(false); }
  };

  const handleRfq = async (e) => {
    e.preventDefault();
    setRfqSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sourcing/rfq`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_name: rfqName, quantity: Number(rfqQty), service_type: rfqService }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('Custom RFQ submitted!');
      setRfqName(''); setRfqQty('');
    } catch (err) { toast.error(err.message); }
    finally { setRfqSubmitting(false); }
  };

  /* ── Place Order ─────────────────────────────────── */
  const buildSkuArr = () => {
    const p = parsedProduct;
    const skuArr = Object.entries(selectedSkus).map(([name, value]) => {
      const opt = p.sku_options?.find((o) => o.name === name);
      const vObj = opt?.values?.find((v) => skuLabel(v) === value);
      return { name, value: vObj ? skuValueForOrder(vObj) : value };
    });
    if (p.item_id) skuArr.push({ name: 'Item ID', value: `${p.platform} #${p.item_id}` });
    if (matchedSku?.sku_id) skuArr.push({ name: 'SKU ID', value: matchedSku.sku_id });
    if (matchedSku?.spec_id) skuArr.push({ name: 'Spec ID', value: matchedSku.spec_id });
    return skuArr;
  };

  const saveForLater = async () => {
    if (!client) { navigate('/sourcing/auth'); return; }
    setSaving(true);
    const p = parsedProduct;
    try {
      const res = await fetch(`${API}/api/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('client_token')}` },
        body: JSON.stringify({
          product_title: p.title, title_zh: p.title_zh || '', platform: p.platform, item_id: p.item_id || '',
          source_url: p.source_url || url, image: allImages[activeGalleryIdx] || allImages[0] || '',
          price_rmb: unitPrice, price_usd: Number((unitPrice / exchangeRate).toFixed(2)), moq: p.moq || '',
          quantity: orderQty, sku_selections: buildSkuArr(), notes: specialInstructions,
          sku_key: Object.entries(selectedSkus).map(([name, value]) => {
            const opt = p.sku_options?.find((o) => o.name === name);
            const vObj = opt?.values?.find((v) => skuLabel(v) === value);
            return `${opt?.name_zh || name}=${vObj?.zh || value}`;
          }).sort().join('|'),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Could not save');
      setSaved(true);
      toast.success(json.duplicate ? 'Already in your saved list' : 'Saved for later', {
        action: { label: 'View saved', onClick: () => navigate('/sourcing/wishlist') },
      });
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const placeOrder = async (auto = false) => {
    if (!client) { savePendingOrder(); toast.message('Log in or register — your order will be placed automatically afterwards'); navigate('/sourcing/auth'); return; }
    setOrderSubmitting(true);
    const p = parsedProduct;
    const skuArr = buildSkuArr();
    const waWin = auto ? null : window.open('', '_blank');
    try {
      const token = localStorage.getItem('client_token');
      const res = await fetch(`${API}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_title: p.title_zh && p.title_zh !== p.title ? `${p.title} | ${p.title_zh}` : p.title,
          product_url: p.source_url || url,
          platform: p.platform,
          price_rmb: unitPrice,
          price_usd: Number((unitPrice / exchangeRate).toFixed(2)),
          quantity: orderQty,
          sku_selections: skuArr,
          special_instructions: specialInstructions,
          domestic_shipping_rmb: p.domestic_shipping_rmb || 0,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Order failed');
      const waUrl = buildWhatsAppUrl(json.data);
      setPlacedOrder({ ...json.data, whatsapp_url: waUrl });
      toast.success(auto ? `Welcome back! Order #${json.data.id} has been placed — tap "Chat with Agent" to send it on WhatsApp` : `Order #${json.data.id} created!`);
      if (auto) return;
      if (waWin) waWin.location.href = waUrl; else window.location.href = waUrl;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setOrderSubmitting(false); }
  };

  const downloadProforma = async () => {
    setProformaBusy(true);
    await downloadPdf(`${API}/api/payments/${placedOrder.id}/proforma`, { Authorization: `Bearer ${localStorage.getItem('client_token')}` }, `Proforma-CIPS-RTR-${placedOrder.id}.pdf`);
    setProformaBusy(false);
  };

  /* ── WhatsApp Message ────────────────────────────── */
  const buildWhatsAppUrl = (order) => {
    const skuText = order.sku_selections?.length ? order.sku_selections.map(s => `  ${s.name}: ${s.value}`).join('\n') : '  None';
    const msg = `*New Order from RunTheRays Sourcing Portal*
━━━━━━━━━━━━━━━
*Order ID:* #${order.id}
*Product:* ${order.product_title}
*Platform:* ${order.platform}
*Link:* ${order.product_url}

*Selected Options:*
${skuText}

*Pricing:*
  Unit Price: ¥${order.price_rmb}/pc
  Quantity: ${order.quantity}
  Domestic Shipping: ¥${order.domestic_shipping_rmb}
  *Total: ¥${order.total_rmb?.toFixed(2)}*

*Special Instructions:*
${order.special_instructions || 'None'}

*Client:* ${order.client_name} (${order.client_whatsapp})
━━━━━━━━━━━━━━━
Please confirm payment details.`;

    return `https://wa.me/${order.agent_whatsapp || WHATSAPP_NUM}?text=${encodeURIComponent(msg)}`;
  };

  const platformColor = (p) => {
    const m = { '1688': 'bg-orange-100 text-orange-700 border-orange-200', 'Taobao': 'bg-red-100 text-red-700 border-red-200', 'Tmall': 'bg-rose-100 text-rose-700 border-rose-200', 'Alibaba': 'bg-yellow-100 text-yellow-700 border-yellow-200', 'Pinduoduo': 'bg-red-100 text-red-700 border-red-200' };
    return m[p] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const orderTotal = parsedProduct ? (unitPrice * orderQty) + (parsedProduct.domestic_shipping_rmb || 0) : 0;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" data-testid="sourcing-portal">
      {/* ── Status Bar ───────────────────────────────── */}
      <div className="bg-blue-950 text-xs py-2.5 px-4 border-b border-blue-800" data-testid="status-bar">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2 text-blue-200">
          <div className="flex items-center gap-4">
            <span className="flex items-center text-emerald-400 font-medium gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Shanghai Warehouse Operational</span>
            <span className="hidden md:inline text-blue-400">|</span>
            <span className="hidden md:flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-orange-400" /> Live Inspection Response: &lt; 2 Hours</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline">info@runtheraysintl.com</span>
            <span className="flex items-center gap-1.5 border-l border-blue-700 pl-3" data-testid="fx-status">
              <span className={`w-2 h-2 rounded-full ${fx?.source === 'live' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-400'}`} />
              <select value={currency} onChange={(e) => changeCurrency(e.target.value)}
                className="bg-blue-900 border border-blue-700 text-orange-400 font-semibold text-xs rounded-md px-1.5 py-0.5 focus:outline-none cursor-pointer"
                data-testid="currency-toggle" aria-label="Display currency">
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
              </select>
              {curRate ? (
                <span className="font-semibold text-orange-400" title={`Mid-market ¥${curRate.market_rmb_per_unit.toFixed(4)} · buffer ${currency === 'GHS' ? `¥${fx.ghs_markup_rmb}` : `${(fx.markup_pct * 100).toFixed(0)}%`} · updated ${new Date(fx.updated_at).toLocaleTimeString()}`} data-testid="fx-rate">
                  1 {currency} = ¥{curRate.rmb_per_unit.toFixed(curRate.rmb_per_unit < 0.1 ? 4 : 2)} <span className="text-blue-300 font-normal hidden sm:inline">· live rate</span>
                </span>
              ) : (
                <span className="font-semibold text-orange-400">USD/RMB {exchangeRate}</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* ── Nav ────────────────────────────────────────── */}
      <nav className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 text-blue-900 font-bold text-base sm:text-lg whitespace-nowrap" data-testid="sourcing-home-link">
            <ArrowLeft className="w-5 h-5" />
            <span>RunTheRays <span className="text-orange-600">Sourcing</span></span>
          </Link>
          {/* Mobile account actions — always visible in portrait */}
          <div className="flex sm:hidden items-center gap-1.5" data-testid="mobile-account-nav">
            {client ? (
              <>
                <Link to="/sourcing/wishlist" className="flex flex-col items-center justify-center min-w-[52px] px-1.5 py-1 rounded-lg text-gray-700 hover:bg-gray-100" data-testid="mobile-nav-wishlist">
                  <Heart className="w-5 h-5" /><span className="text-[10px] font-semibold leading-tight">Saved</span>
                </Link>
                <Link to="/sourcing/orders" className="relative flex flex-col items-center justify-center min-w-[52px] px-1.5 py-1 rounded-lg text-gray-700 hover:bg-gray-100" data-testid="mobile-nav-my-orders">
                  <Package className="w-5 h-5" /><span className="text-[10px] font-semibold leading-tight">Orders</span>
                  {pendingQc > 0 && <span className="absolute -top-0.5 right-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold animate-pulse" data-testid="mobile-nav-qc-badge">{pendingQc}</span>}
                </Link>
                <button onClick={() => { logout(); }} className="flex flex-col items-center justify-center min-w-[48px] px-1.5 py-1 rounded-lg text-red-500 hover:bg-red-50" aria-label="Logout" data-testid="mobile-nav-logout">
                  <LogOut className="w-5 h-5" /><span className="text-[10px] font-semibold leading-tight">Logout</span>
                </button>
              </>
            ) : (
              <Link to="/sourcing/auth" className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap" data-testid="mobile-nav-login-btn">
                <User className="w-4 h-4" /> Login / Register
              </Link>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-5 text-sm font-medium text-gray-600">
            <a href="#link-parser" className="hover:text-blue-900 flex items-center gap-1 text-orange-600"><Zap className="w-4 h-4" /> Fast Sourcing</a>
            <a href="#how-it-works" className="hover:text-blue-900">How It Works</a>
            <a href="#calculator" className="hover:text-blue-900">Calculator</a>
            {client ? (
              <>
                <Link to="/sourcing/wishlist" className="hover:text-blue-900 flex items-center gap-1" data-testid="nav-wishlist"><Heart className="w-4 h-4" /> Saved</Link>
                <Link to="/sourcing/orders" className="hover:text-blue-900 flex items-center gap-1 relative" data-testid="nav-my-orders"><Package className="w-4 h-4" /> My Orders
                  {pendingQc > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold animate-pulse" data-testid="nav-qc-badge">{pendingQc}</span>}
                </Link>
                <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                  <span className="text-gray-700 text-xs"><User className="w-3.5 h-3.5 inline mr-1" />{client.name}</span>
                  <button onClick={() => { logout(); }} className="text-red-500 hover:text-red-700" data-testid="nav-logout"><LogOut className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <Link to="/sourcing/auth" className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors" data-testid="nav-login-btn">
                <User className="w-4 h-4" /> Login / Register
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ══ HERO + 5 STEP ═══════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-950 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=60&w=2000')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-950/95 to-blue-950" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-xs sm:text-sm font-medium mb-5">
              <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" /> Next-Gen B2B Sourcing & Verification Engine
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight mb-4">
              Global Trade Made Simple.{' '}<span className="text-orange-400">Direct From Shanghai.</span>
            </h1>
            <p className="text-blue-200 text-base sm:text-lg max-w-2xl mx-auto">
              Paste any product link from <strong className="text-white">1688, Taobao, Pinduoduo, or Alibaba</strong>. We verify, negotiate, inspect, and ship worldwide.
            </p>
          </div>
          <div id="how-it-works" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {STEPS.map((s) => { const Icon = s.icon; return (
              <div key={s.num} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-orange-400/40 transition-all group" data-testid={`step-${s.num}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold text-orange-400 uppercase">Step {s.num}</span>
                  <Icon className="w-4 h-4 text-blue-300 group-hover:text-orange-400 transition-colors" />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">{s.title}</h3>
                <p className="text-blue-300 text-[11px] leading-relaxed">{s.desc}</p>
              </div>
            ); })}
          </div>
        </div>
      </section>

      {/* ══ LINK PARSER ═════════════════════════════════ */}
      <section id="link-parser" className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6 relative z-20 mb-14">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-6" data-testid="link-parser-card" style={{ minWidth: 0 }}>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 border-b border-gray-100 pb-3 min-w-0 [&>button]:whitespace-nowrap [&>button]:flex-shrink-0 [&>button]:px-3 sm:[&>button]:px-4 [&>button]:text-xs sm:[&>button]:text-sm" data-testid="search-mode-tabs">
            <button onClick={() => setMode('link')} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${mode === 'link' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`} data-testid="tab-link-mode">
              <Link2 className="w-4 h-4" /> Paste Link
            </button>
            <button onClick={() => setMode('keyword')} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${mode === 'keyword' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`} data-testid="tab-keyword-mode">
              <Search className="w-4 h-4" /> Keyword Search
            </button>
            <button onClick={() => setMode('photo')} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${mode === 'photo' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`} data-testid="tab-photo-mode">
              <Camera className="w-4 h-4" /> Photo Upload
            </button>
            <button onClick={() => setMode('estimator')} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${mode === 'estimator' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`} data-testid="tab-estimator-mode">
              <Truck className="w-4 h-4" /> Instant Freight Estimator
            </button>
            <button onClick={() => setMode('manual')} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${mode === 'manual' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`} data-testid="tab-manual-mode">
              <FileText className="w-4 h-4" /> Custom Inspection
            </button>
          </div>

          {mode === 'keyword' && <KeywordSearch currency={currency} onSelect={openSearchResult} loadingId={resultLoadingId} />}
          {mode === 'photo' && <PhotoSearch currency={currency} onSelect={openSearchResult} loadingId={resultLoadingId} />}
          {mode === 'estimator' && <FreightEstimator currency={currency} fx={fx} />}

          {mode === 'link' && (
            <div className="space-y-3">
              <form onSubmit={handleParse} className="flex flex-col md:flex-row gap-2" data-testid="url-parser-form">
                <div className="relative flex-1">
                  <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
                  <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="Paste a product link, app share text, or item ID (e.g. 977684561375)..."
                    className="w-full pl-11 pr-20 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-sm"
                    required data-testid="product-url-input" />
                  <button type="button" onClick={handlePaste} className="absolute right-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md font-medium">Paste</button>
                </div>
                <Button type="submit" disabled={parsing} className="min-w-[140px] bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl shadow-md" data-testid="parse-btn">
                  {parsing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}
                  {parsing ? 'Searching...' : 'Search'}
                </Button>
              </form>
              <div className="flex flex-wrap items-center justify-between text-xs text-gray-400 px-1 gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-500 font-medium">Supported:</span>
                  {['1688.com', 'Taobao', 'Alibaba', 'Tmall', 'Pinduoduo'].map((p) => (
                    <span key={p} className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 font-medium">{p}</span>
                  ))}
                </div>
                <span className="text-emerald-600 flex items-center gap-1 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Direct Listing Data — No AI Guesswork</span>
              </div>
              <p className="text-xs text-gray-500 px-1" data-testid="link-input-hint">
                Accepts desktop links, mobile share text, and raw item IDs. 1688 app share codes (e.g. ￥R652zva0Qx￥) can't be opened outside the app — use <span className="font-medium text-gray-700">Share → Copy Link</span> instead.
              </p>
              {parsing && (
                <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                  <div className="flex items-center justify-center gap-3 text-blue-700 font-medium"><Loader2 className="w-5 h-5 animate-spin" /> Reading item ID and fetching the live listing...</div>
                </div>
              )}
              {linkInfo && !parsing && (
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 text-left" data-testid="link-fallback-card">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${platformColor(linkInfo.data.platform)}`} data-testid="fallback-platform">{linkInfo.data.platform}</span>
                    <span className="text-sm font-semibold text-gray-900">Item ID detected: <span className="font-mono" data-testid="fallback-item-id">{linkInfo.data.item_id}</span></span>
                  </div>
                  <p className="text-sm text-amber-800 mb-4" data-testid="fallback-message">
                    {linkInfo.status === 'not_found' ? 'Item not found on the platform. ' : linkInfo.status === 'unavailable' ? 'Live listing data is not connected yet. ' : ''}
                    {linkInfo.message} Enter the item details below and our agent will verify it on the original listing.
                  </p>
                  <form onSubmit={openManualProduct} className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2" data-testid="manual-entry-form">
                    <input type="text" value={manualTitle} onChange={(e) => setManualTitle(e.target.value)} placeholder="Product name (as shown on the listing)" required
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:border-orange-500 focus:outline-none" data-testid="manual-title-input" />
                    <input type="number" step="0.01" min="0" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="Price ¥ (optional)"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:border-orange-500 focus:outline-none" data-testid="manual-price-input" />
                    <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl" data-testid="manual-continue-btn">Continue</Button>
                  </form>
                  <a href={linkInfo.data.source_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600">
                    <ExternalLink className="w-3 h-3" /> Open original listing
                  </a>
                </div>
              )}
            </div>
          )}

          {mode === 'manual' && (
            <form onSubmit={handleRfq} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="rfq-form">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Product Name / Category</label>
                <input type="text" value={rfqName} onChange={(e) => setRfqName(e.target.value)} placeholder="e.g., Solar Inverters" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none" required data-testid="rfq-product-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Target Quantity</label>
                <input type="number" value={rfqQty} onChange={(e) => setRfqQty(e.target.value)} placeholder="500" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none" required data-testid="rfq-quantity" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Service Type</label>
                <select value={rfqService} onChange={(e) => setRfqService(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none" data-testid="rfq-service-type">
                  <option>Full Sourcing & Shipping</option>
                  <option>Factory Audit & Inspection Only</option>
                  <option>Sample Consolidation</option>
                </select>
              </div>
              <div className="md:col-span-3 flex justify-end mt-2">
                <Button type="submit" disabled={rfqSubmitting} className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-2.5 rounded-xl" data-testid="rfq-submit-btn">
                  {rfqSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Custom RFQ
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ══ TRUST STATS ═════════════════════════════════ */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-stats">
          {[{ val: '300+', label: 'Successful Projects', accent: true }, { val: '100+', label: 'Verified Manufacturers' }, { val: '0%', label: 'Scam & Defect Rate', accent: true }, { val: '24h', label: 'Shanghai Warehouse QC' }].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-5 text-center hover:border-orange-300 hover:shadow-md transition-all">
              <div className={`text-3xl font-extrabold mb-1 ${s.accent ? 'text-orange-600' : 'text-blue-900'}`}>{s.val}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CALCULATOR ══════════════════════════════════ */}
      <section id="calculator" className="py-14 bg-blue-950 border-t border-b border-blue-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Instant B2B Cost & Freight Estimator</h2>
            <p className="text-blue-300 text-sm mt-2">Quick estimates for warehousing, inspection, and freight.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 bg-blue-900/50 backdrop-blur border border-blue-700/50 rounded-2xl p-6" data-testid="calculator-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div><label className="block text-xs font-semibold text-blue-200 mb-1.5">Cargo Weight (KG)</label><input type="number" value={calcWeight} onChange={(e) => setCalcWeight(Number(e.target.value))} min="1" className="w-full px-3.5 py-2.5 bg-blue-950 border border-blue-600 rounded-xl text-white text-sm focus:border-orange-400 focus:outline-none" data-testid="calc-weight" /></div>
                <div><label className="block text-xs font-semibold text-blue-200 mb-1.5">Volume (CBM)</label><input type="number" value={calcCbm} onChange={(e) => setCalcCbm(Number(e.target.value))} step="0.1" min="0.1" className="w-full px-3.5 py-2.5 bg-blue-950 border border-blue-600 rounded-xl text-white text-sm focus:border-orange-400 focus:outline-none" data-testid="calc-cbm" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div><label className="block text-xs font-semibold text-blue-200 mb-1.5">Shipping Method</label>
                  <select value={calcMethod} onChange={(e) => setCalcMethod(e.target.value)} className="w-full px-3.5 py-2.5 bg-blue-950 border border-blue-600 rounded-xl text-white text-sm focus:border-orange-400 focus:outline-none" data-testid="calc-method">
                    <option value="sea">Sea Freight (LCL)</option><option value="air">Air Express</option>
                  </select>
                </div>
                <div className="space-y-2 pt-5">
                  <label className="flex items-center gap-2 text-xs text-blue-200 cursor-pointer"><input type="checkbox" checked={calcQc} onChange={(e) => setCalcQc(e.target.checked)} className="rounded border-blue-600 text-orange-500 focus:ring-0" data-testid="calc-qc" /> HD QC Photos (${QC_PHOTO_FEE})</label>
                  <label className="flex items-center gap-2 text-xs text-blue-200 cursor-pointer"><input type="checkbox" checked={calcAudit} onChange={(e) => setCalcAudit(e.target.checked)} className="rounded border-blue-600 text-orange-500 focus:ring-0" data-testid="calc-audit" /> Factory Audit (${FACTORY_AUDIT_FEE})</label>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 bg-gradient-to-br from-blue-900 to-blue-950 border border-orange-500/30 rounded-2xl p-6 shadow-lg" data-testid="calculator-result">
              <h3 className="text-sm uppercase tracking-wider text-orange-400 font-bold mb-4 flex items-center justify-between">Cost Breakdown <Calculator className="w-4 h-4" /></h3>
              <div className="space-y-3 text-sm border-b border-blue-700/50 pb-4">
                <div className="flex justify-between text-blue-300"><span>Freight:</span><span className="font-medium text-white" data-testid="res-freight">${freight.toFixed(2)}</span></div>
                <div className="flex justify-between text-blue-300"><span>Handling:</span><span className="font-medium text-white">${HANDLING_FEE.toFixed(2)}</span></div>
                <div className="flex justify-between text-blue-300"><span>Inspection:</span><span className="font-medium text-white" data-testid="res-qc">${qcCost.toFixed(2)}</span></div>
              </div>
              <div className="pt-4 mb-5">
                <div className="text-xs text-blue-400">Total Estimated Cost</div>
                <div className="text-3xl font-extrabold text-orange-400 mt-1" data-testid="res-total">${total.toFixed(2)}</div>
                <p className="text-[11px] text-blue-400 mt-1">*Final confirmed at Shanghai Warehouse.</p>
              </div>
              <Button onClick={() => toast.success('Estimate locked!')} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl" data-testid="lock-estimate-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Lock In This Estimate
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 border-t border-blue-800 py-8 text-xs text-blue-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="text-white font-semibold text-sm">Shanghai RunTheRays Co., Ltd.</span>
          <span>Official Sourcing & Logistics Partner | Shanghai Warehouse Hub</span>
          <span className="text-blue-400">&copy; 2026 Shanghai RunTheRays International.</span>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 bg-emerald-500 hover:bg-emerald-600 text-white p-3.5 rounded-full shadow-xl transition-all hover:scale-110" data-testid="whatsapp-float">
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* ══ PRODUCT MODAL WITH SKU + ORDER ══════════════ */}
      {showModal && parsedProduct && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm" data-testid="product-modal">
          <div className="bg-white max-w-2xl w-full rounded-t-2xl sm:rounded-2xl shadow-2xl relative max-h-[94vh] sm:max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 p-1 z-10" data-testid="modal-close-btn"><X className="w-6 h-6" /></button>

            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3"><CheckCircle2 className="w-4 h-4" /> Product Extracted</div>

              {/* Gallery + Info */}
              <div className="flex flex-col sm:flex-row gap-5 mb-5 pb-4 border-b border-gray-200">
                {/* Gallery */}
                <div className="flex-shrink-0 sm:w-56" data-testid="product-gallery">
                  <div
                    className="w-full aspect-square rounded-xl bg-gray-100 border border-gray-200 overflow-hidden mb-2 cursor-pointer relative group"
                    onClick={() => allImages.length > 0 && setLightboxOpen(true)}
                    data-testid="product-main-image"
                  >
                    {allImages.length > 0 ? (
                      <>
                        <img
                          src={imgSrc(allImages[activeGalleryIdx] || allImages[0])}
                          alt={parsedProduct.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                        </div>
                        <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium">{allImages.length} photos</span>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 text-xs gap-1"><Package className="w-12 h-12 text-gray-300" />{parsedProduct.manual_entry ? 'Manual entry' : 'No image'}</div>
                    )}
                  </div>
                  {parsedProduct.gallery_images?.length > 1 && (
                    <div className="flex gap-1.5 overflow-x-auto">
                      {parsedProduct.gallery_images.map((img, i) => (
                        <button key={i} onClick={() => setActiveGalleryIdx(i)}
                          className={`w-12 h-12 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all ${activeGalleryIdx === i ? 'border-orange-500 shadow-md' : 'border-gray-200 opacity-70 hover:opacity-100'}`}>
                          <img src={imgSrc(img)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${platformColor(parsedProduct.platform)}`}>{parsedProduct.platform}</span>
                    {parsedProduct.item_id && <span className="text-[10px] text-gray-400 font-mono" data-testid="modal-item-id">ID {parsedProduct.item_id}</span>}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 leading-snug" data-testid="modal-prod-title">{parsedProduct.title}</h3>
                  {parsedProduct.title_zh && parsedProduct.title_zh !== parsedProduct.title && <p className="text-xs text-gray-400 mt-0.5">{parsedProduct.title_zh}</p>}
                  <div className="flex items-baseline gap-3 mt-2">
                    <span className="text-2xl font-extrabold text-orange-600" data-testid="modal-prod-price">{unitPrice > 0 ? `¥${unitPrice.toFixed(2)}` : 'Price on request'}</span>
                    {unitPrice > 0 && fmtGhs(unitPrice) && <span className="text-sm font-bold text-emerald-700" data-testid="modal-prod-price-ghs">≈ {fmtGhs(unitPrice)}</span>}
                    {unitPrice > 0 && <span className="text-sm text-gray-400">(${(unitPrice / exchangeRate).toFixed(2)} USD)</span>}
                    {unitPrice > 0 && <span className="text-xs text-gray-400">/unit</span>}
                  </div>
                  {!matchedSku && parsedProduct.price_max_rmb > parsedProduct.price_rmb && <p className="text-xs text-gray-500">Range ¥{parsedProduct.price_rmb.toFixed(2)} – ¥{parsedProduct.price_max_rmb.toFixed(2)} depending on options</p>}
                  {parsedProduct.price_tiers?.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="price-tiers">
                      {parsedProduct.price_tiers.map((t) => (
                        <span key={t.min_qty} className={`px-2 py-0.5 rounded border text-[11px] ${tierPrice === t.price_rmb ? 'bg-orange-50 border-orange-300 text-orange-700 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                          ≥{t.min_qty} pcs · ¥{t.price_rmb.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  )}
                  {parsedProduct.moq && <p className="text-xs text-gray-500 mt-1">MOQ: {parsedProduct.moq}</p>}
                  {parsedProduct.seller && <p className="text-xs text-gray-500">Seller: {parsedProduct.seller}{parsedProduct.location ? ` · ${parsedProduct.location}` : ''}</p>}
                  {parsedProduct.domestic_shipping_rmb > 0 && <p className="text-xs text-emerald-600 mt-0.5">Domestic Shipping: ¥{parsedProduct.domestic_shipping_rmb}</p>}
                  {parsedProduct.domestic_shipping_rmb === 0 && <p className="text-xs text-emerald-600 font-medium mt-0.5">Free Domestic Shipping</p>}
                  {parsedProduct.supplier_type && <p className="text-[11px] text-blue-600 mt-1 font-medium">{parsedProduct.supplier_type}</p>}

                  {parsedProduct.description && <p className="text-xs text-gray-500 mt-2 leading-relaxed">{parsedProduct.description}</p>}
                </div>
              </div>

              {/* SKU Options */}
              {parsedProduct.sku_options?.length > 0 && (
                <div className="mb-5 space-y-3" data-testid="sku-options">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">Select Options</h4>
                  {parsedProduct.sku_options.map((opt) => {
                    const expanded = !!expandedSku[opt.name];
                    const values = expanded ? opt.values : opt.values.slice(0, SKU_PREVIEW);
                    return (
                    <div key={opt.name}>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">{opt.name}{opt.name_zh && opt.name_zh !== opt.name ? <span className="text-gray-400 font-normal"> · {opt.name_zh}</span> : null}
                        {selectedSkus[opt.name] && <span className="ml-2 text-xs text-orange-600 font-semibold">✓ {selectedSkus[opt.name]}</span>}
                      </label>
                      <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                        {values.map((v) => {
                          const label = skuLabel(v);
                          const zh = typeof v === 'string' ? '' : v.zh;
                          const vImg = typeof v === 'string' ? '' : v.image;
                          const active = selectedSkus[opt.name] === label;
                          const skuPrice = parsedProduct.sku_list?.find((s) => Object.keys(s.props || {}).length === 1 && s.props[opt.name] === label)?.price_rmb;
                          return (
                            <button key={label} type="button"
                              onClick={() => selectSku(opt.name, label, vImg)}
                              className={`flex items-center gap-3 sm:gap-1.5 w-full sm:w-auto text-left px-3 py-2.5 sm:py-1.5 rounded-xl sm:rounded-lg text-sm border transition-all active:scale-[0.99] ${active ? 'bg-orange-600 text-white border-orange-600 shadow-md' : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'}`}
                              data-testid={`sku-${opt.name}-${label}`}>
                              {vImg && <img src={imgSrc(vImg)} alt="" className="w-10 h-10 sm:w-6 sm:h-6 rounded-lg sm:rounded object-cover border border-white/40 flex-shrink-0" data-testid="sku-thumb" />}
                              <span className="flex-1 min-w-0 sm:flex sm:items-center sm:gap-1">
                                <span className="block sm:inline font-medium leading-tight">{label}</span>
                                {zh && zh !== label ? <span className={`block sm:inline text-xs mt-0.5 sm:mt-0 sm:ml-1 ${active ? 'text-orange-100' : 'text-gray-400'}`}>{zh}</span> : null}
                              </span>
                              {skuPrice ? <span className={`text-xs font-bold sm:hidden ${active ? 'text-white' : 'text-orange-600'}`}>¥{skuPrice.toFixed(2)}</span> : null}
                              {active && <CheckCircle2 className="w-4 h-4 sm:hidden flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                      {opt.values.length > SKU_PREVIEW && (
                        <button type="button" onClick={() => setExpandedSku((p) => ({ ...p, [opt.name]: !expanded }))}
                          className="mt-2 text-xs font-semibold text-orange-600 hover:underline" data-testid={`sku-toggle-${opt.name}`}>
                          {expanded ? 'Show fewer options' : `Show all ${opt.values.length} options`}
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Specs */}
              {parsedProduct.specs?.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Specifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {parsedProduct.specs.map((s, i) => <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg border border-blue-100">{s}</span>)}
                  </div>
                </div>
              )}

              {/* Order Section */}
              {placedOrder ? (
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 text-center space-y-3" data-testid="order-success-panel">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                  <h4 className="font-bold text-gray-900">Order #{placedOrder.id} placed</h4>
                  <p className="text-sm text-gray-600">Your order is saved under <strong>My Orders</strong>. Send it to your agent on WhatsApp, then pay via Stanbic CIPS using the proforma invoice.</p>
                  <a href={placedOrder.whatsapp_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl shadow-md transition-colors"
                    data-testid="order-whatsapp-link">
                    <MessageCircle className="w-5 h-5" /> Chat with Agent on WhatsApp
                  </a>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button type="button" variant="outline" onClick={downloadProforma} disabled={proformaBusy} className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold rounded-xl" data-testid="order-proforma-btn">
                      {proformaBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />} Download Proforma Invoice
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate(`/sourcing/orders?highlight=${placedOrder.id}`)} className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50 font-semibold rounded-xl" data-testid="order-pay-now-btn">
                      <Landmark className="w-4 h-4 mr-2" /> Pay via Stanbic CIPS
                    </Button>
                  </div>
                  <div className="flex justify-center gap-4 text-sm">
                    <Link to={`/sourcing/orders?highlight=${placedOrder.id}`} className="text-orange-600 hover:underline font-medium" data-testid="order-view-orders-link">View my orders</Link>
                    <button onClick={() => setShowModal(false)} className="text-gray-500 hover:underline" data-testid="order-continue-btn">Continue sourcing</button>
                  </div>
                </div>
              ) : (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-4" data-testid="order-section" id="order-section" ref={orderSectionRef}>
                <h4 className="font-bold text-gray-900 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-orange-600" /> Buy via Agent</h4>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">Quantity:</label>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button type="button" onClick={() => setOrderQty(Math.max(1, orderQty - 1))} className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600"><Minus className="w-4 h-4" /></button>
                    <input type="number" value={orderQty} onChange={(e) => setOrderQty(Math.max(1, Number(e.target.value)))} min="1" className="w-20 text-center py-2 border-x border-gray-200 text-sm font-semibold focus:outline-none" data-testid="order-qty-input" />
                    <button type="button" onClick={() => setOrderQty(orderQty + 1)} className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Special Instructions</label>
                  <textarea value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} rows={2} placeholder="Color, logo printing, custom packaging..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-orange-500 focus:outline-none" data-testid="order-instructions" />
                </div>

                {/* Price summary */}
                <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-600"><span>Unit Price:</span><span>{unitPrice > 0 ? `¥${unitPrice.toFixed(2)}` : 'TBC by agent'}</span></div>
                  <div className="flex justify-between text-gray-600"><span>Qty:</span><span>x {orderQty}</span></div>
                  {(parsedProduct.domestic_shipping_rmb || 0) > 0 && <div className="flex justify-between text-gray-600"><span>Domestic Shipping:</span><span>¥{parsedProduct.domestic_shipping_rmb}</span></div>}
                  <div className="flex justify-between font-bold text-gray-900 border-t border-gray-100 pt-1.5">
                    <span>Total:</span>
                    <span className="text-orange-600 text-lg" data-testid="order-total">¥{orderTotal.toFixed(2)}</span>
                  </div>
                  {fmtGhs(orderTotal) && (
                    <div className="flex justify-between text-emerald-700 font-semibold text-xs">
                      <span>In {currencyMeta(currency).name} (1 {currency} = ¥{curRate.rmb_per_unit.toFixed(curRate.rmb_per_unit < 0.1 ? 4 : 2)}):</span>
                      <span data-testid="order-total-ghs">≈ {fmtGhs(orderTotal)}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => placeOrder()} disabled={orderSubmitting}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl shadow-md"
                    data-testid="place-order-btn">
                    {orderSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                    {client ? 'Place Order & Send to Agent' : 'Login to Place Order'}
                  </Button>
                  {!client && (
                  <Button type="button" onClick={shareToWhatsApp} variant="outline"
                    className="sm:w-auto border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-bold py-3 rounded-xl"
                    data-testid="share-whatsapp-btn">
                    <Share2 className="w-4 h-4 mr-2" /> Share to Agent
                  </Button>
                  )}
                  <Button type="button" onClick={saveForLater} disabled={saving || saved} variant="outline"
                    className={`sm:w-auto font-bold py-3 rounded-xl ${saved ? 'border-orange-500 text-orange-600 bg-orange-50' : 'border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600'}`}
                    data-testid="save-later-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className={`w-4 h-4 mr-2 ${saved ? 'fill-orange-600' : ''}`} />}
                    {saved ? 'Saved' : 'Save for Later'}
                  </Button>
                </div>

                {!client && (
                  <p className="text-xs text-center text-gray-500">
                    <Link to="/sourcing/auth" className="text-orange-600 hover:underline font-medium">Login or register</Link> to place your order, track it under My Orders and download a proforma invoice. "Share to Agent" only sends an inquiry — it does not create an order.
                  </p>
                )}
              </div>
              )}

              {parsedProduct.source_url && (
                <a href={parsedProduct.source_url} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors">
                  <ExternalLink className="w-3 h-3" /> View original listing
                </a>
              )}
            </div>
            {/* Mobile sticky CTA */}
            {!placedOrder && !orderSectionVisible && (
              <div className="sm:hidden sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 flex items-center gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]" data-testid="mobile-order-bar">
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-500 leading-none">Total · qty {orderQty}</p>
                  <p className="text-lg font-bold text-orange-600 leading-tight">¥{(unitPrice * orderQty).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <Button onClick={() => orderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-bold py-3 rounded-xl" data-testid="mobile-order-bar-btn">
                  <ShoppingCart className="w-4 h-4 mr-2" /> {client ? 'Order now' : 'Login to order'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ FULLSCREEN LIGHTBOX GALLERY ══════════════════ */}
      {resuming && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center" data-testid="order-resuming-overlay">
          <div className="bg-white rounded-2xl px-6 py-5 shadow-xl flex items-center gap-3 text-gray-800 font-semibold"><Loader2 className="w-5 h-5 animate-spin text-orange-600" /> Welcome back — restoring your order…</div>
        </div>
      )}
      {lightboxOpen && allImages.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" data-testid="image-lightbox" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10" onClick={() => setLightboxOpen(false)} data-testid="lightbox-close">
            <X className="w-8 h-8" />
          </button>

          {/* Navigation arrows */}
          {allImages.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-10 backdrop-blur-sm" data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setActiveGalleryIdx((activeGalleryIdx - 1 + allImages.length) % allImages.length); }}>
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-10 backdrop-blur-sm" data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); setActiveGalleryIdx((activeGalleryIdx + 1) % allImages.length); }}>
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Main image */}
          <div className="max-w-3xl max-h-[80vh] mx-auto px-16" onClick={(e) => e.stopPropagation()}>
            <img
              src={imgSrc(allImages[activeGalleryIdx] || allImages[0])}
              alt={parsedProduct.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl mx-auto"
              data-testid="lightbox-image"
            />
            <div className="text-center mt-4 text-white/70 text-sm" data-testid="lightbox-counter">
              {activeGalleryIdx + 1} / {allImages.length}
              {activeGalleryIdx >= (parsedProduct.gallery_images?.length || 0) ? ' · Detail photo' : ' · Product photo'} — {parsedProduct.title}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-2">
            {allImages.map((img, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setActiveGalleryIdx(i); }}
                className={`w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeGalleryIdx === i ? 'border-orange-500 scale-110' : 'border-white/30 opacity-60 hover:opacity-100'}`}>
                <img src={imgSrc(img)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
